import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import { useData } from "../hooks/useData";
import { useFirebase } from "../hooks/useFirebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import {
  IText,
  Image,
  Rect,
  Circle,
  Line,
  Polygon,
  Polyline,
  Path,
  Group,
} from "fabric";

const OpenFromMyAccountDialog = ({ open, onClose, canvas, onOpenSuccess }) => {
  const { setProjectName, setSnackbar } = useData();
  const { firestore, mivaObj } = useFirebase();
  const [savedFiles, setSavedFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState("");
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isLoadingFileObject, setIsLoadingFileObject] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Map of Fabric.js types to their classes
  const fabricClasses = {
    rect: Rect,
    circle: Circle,
    line: Line,
    polygon: Polygon,
    polyline: Polyline,
    path: Path,
    group: Group,
    "i-text": IText,
    IText: IText,
    image: Image,
    Image: Image,
  };

  // Fetch saved files from Firebase when dialog opens
  useEffect(() => {
    if (!open || !firestore || !mivaObj?.customer_id) return;

    const fetchFiles = async () => {
      setIsLoadingFiles(true);
      try {
        const customerId = mivaObj.customer_id;
        const q = query(
          collection(firestore, "customerSavedFiles"),
          where("customerId", "==", customerId)
        );
        const querySnapshot = await getDocs(q);

        const firestoreResult = [];
        querySnapshot.forEach((doc) => {
          const project = {
            id: doc.id,
            fileName: doc.data().fileName,
            canvasObj: doc.data().canvasObj,
            customerId: doc.data().customerId,
          };
          firestoreResult.push(project);
        });

        setSavedFiles(firestoreResult);
      } catch (error) {
        console.error("Error fetching saved files:", error);
        setSnackbar({
          open: true,
          message: "Failed to fetch saved files.",
          severity: "error",
        });
      } finally {
        setIsLoadingFiles(false);
      }
    };

    fetchFiles();
  }, [open, firestore, mivaObj, setSnackbar]);

  // Load the selected file onto the canvas
  const handleOpenFile = async () => {
    if (!selectedFile || !canvas) return;

    setIsLoadingFileObject(true);
    try {
      const result = savedFiles.find((obj) => obj.fileName === selectedFile);
      if (!result) {
        throw new Error("Selected file not found.");
      }

      // Clear the current canvas
      const canvasObjects = canvas.getObjects();
      if (canvasObjects) {
        canvas.remove(...canvasObjects);
      }

      // Load the new canvas objects
      const enlivenObject = JSON.parse(result.canvasObj);
      console.log(
        "OpenFromMyAccountDialog: enlivenObject (full)",
        enlivenObject
      );

      if (!Array.isArray(enlivenObject.objects)) {
        throw new Error(
          "Invalid file data: enlivenObject.objects is not an array"
        );
      }

      // Manually create Fabric.js objects
      const createdObjects = [];
      for (const objData of enlivenObject.objects) {
        let fabricType = objData.type;
        try {
          let obj;
          if (fabricType === "i-text" || fabricType === "IText") {
            // Explicitly create IText with string text
            const text = String(objData.text || "Placeholder");
            console.log(
              `OpenFromMyAccountDialog: IText text: ${text}, type: ${typeof text}`
            );
            obj = new IText(text, {
              fontSize: objData.fontSize,
              fontFamily: objData.fontFamily,
              fontWeight: objData.fontWeight,
              fontStyle: objData.fontStyle,
              lineHeight: objData.lineHeight,
              left: objData.left,
              top: objData.top,
              fill: objData.fill,
              textAlign: objData.textAlign,
              scaleX: objData.scaleX,
              scaleY: objData.scaleY,
              angle: objData.angle,
              opacity: objData.opacity,
              shadow: objData.shadow,
              stroke: objData.stroke,
              strokeWidth: objData.strokeWidth,
              strokeUniform: objData.strokeUniform,
              charSpacing: objData.charSpacing,
              styles: objData.styles || [],
            });
          } else if (fabricType === "image" || fabricType === "Image") {
            // Preload image to avoid drawImage errors
            if (!objData.src || typeof objData.src !== "string") {
              console.warn(
                `OpenFromMyAccountDialog: Invalid src for Image: ${objData.src}`
              );
              continue;
            }
            const img = await new Promise((resolve, reject) => {
              const imgElement = new Image();
              imgElement.src = objData.src;
              imgElement.crossOrigin = objData.crossOrigin || "anonymous";
              imgElement.onload = () => resolve(imgElement);
              imgElement.onerror = () =>
                reject(new Error(`Failed to load image: ${objData.src}`));
            });
            obj = new Image(img, {
              left: objData.left,
              top: objData.top,
              width: objData.width,
              height: objData.height,
              scaleX: objData.scaleX,
              scaleY: objData.scaleY,
              angle: objData.angle,
              opacity: objData.opacity,
              fill: objData.fill,
              stroke: objData.stroke,
              strokeWidth: objData.strokeWidth,
              strokeUniform: objData.strokeUniform,
              filters: objData.filters || [],
              cropX: objData.cropX,
              cropY: objData.cropY,
              crossOrigin: objData.crossOrigin,
            });
          } else {
            // Attempt to create other object types using the fabricClasses map
            const FabricClass = fabricClasses[fabricType];
            if (FabricClass) {
              obj = new FabricClass(objData);
            } else {
              console.warn(
                `OpenFromMyAccountDialog: Unsupported object type ${fabricType}`
              );
              continue;
            }
          }
          createdObjects.push(obj);
          canvas.add(obj);
        } catch (err) {
          console.warn(
            `OpenFromMyAccountDialog: Failed to create object of type ${fabricType}`,
            err
          );
        }
      }

      if (createdObjects.length === 0) {
        throw new Error("No valid objects created from file");
      }

      canvas.requestRenderAll();
      setSnackbar({
        open: true,
        message: `File loaded successfully (${createdObjects.length}/${enlivenObject.objects.length} objects)`,
        severity:
          createdObjects.length < enlivenObject.objects.length
            ? "warning"
            : "success",
      });
      setProjectName(selectedFile);
      onOpenSuccess?.();
    } catch (error) {
      console.error("Error loading file:", error);
      setSnackbar({
        open: true,
        message: `Failed to load the selected file: ${error.message}`,
        severity: "error",
      });
    } finally {
      setIsLoadingFileObject(false);
      setSelectedFile("");
      onClose();
    }
  };

  // Draggable functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - dialogPosition.x,
      y: e.clientY - dialogPosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDialogPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: 400,
          transform: `translate(${dialogPosition.x}px, ${dialogPosition.y}px)`,
        },
      }}
      aria-labelledby="open-my-account-dialog-title"
      maxWidth="sm"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <DialogTitle
        id="open-my-account-dialog-title"
        sx={{
          bgcolor: "primary.main",
          color: "white",
          fontSize: "1.25rem",
          cursor: "move",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          pb: 2,
        }}
        onMouseDown={handleMouseDown}
      >
        <DragIndicatorOutlinedIcon sx={{ fontSize: 20, mr: 1 }} />
        Open From My Account
      </DialogTitle>
      <DialogContent sx={{ mt: 2, pt: 2, pb: 3 }}>
        {isLoadingFiles ? (
          <Stack direction="column" spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="body1">Loading saved files...</Typography>
          </Stack>
        ) : savedFiles.length === 0 ? (
          <Stack direction="column" spacing={2} alignItems="center">
            <Typography variant="body1">No saved files found.</Typography>
            <Button
              onClick={onClose}
              variant="contained"
              color="secondary"
              sx={{ mt: 1 }}
            >
              Close
            </Button>
          </Stack>
        ) : (
          <Stack direction="column" spacing={3}>
            <FormControl fullWidth sx={{ maxWidth: 350, mt: 2 }}>
              <InputLabel id="select-file-label">Select File</InputLabel>
              <Select
                labelId="select-file-label"
                id="select-file"
                value={selectedFile}
                label="Select File"
                onChange={(e) => setSelectedFile(e.target.value)}
              >
                {savedFiles.map((file) => (
                  <MenuItem key={file.id} value={file.fileName}>
                    {file.fileName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DialogActions sx={{ justifyContent: "flex-end", px: 0 }}>
              <Button onClick={onClose} color="secondary">
                Cancel
              </Button>
              <Button
                onClick={handleOpenFile}
                disabled={!selectedFile || isLoadingFileObject}
                variant="contained"
                color="primary"
              >
                {isLoadingFileObject ? "Loading..." : "Open"}
              </Button>
            </DialogActions>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OpenFromMyAccountDialog;
